import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import type { Expense, ExpensePush, ExpenseQbState } from "@clevis/shared-types";
import { Button, ConfirmButton, Select, cn } from "@clevis/shared-ui";
import { call } from "../lib/callFunction";
import { money } from "./ExpenseEditor";

// Send an expense to QuickBooks as a vendor Bill. The preview is the server's
// own plan (the same one the send runs), so what you see is what posts:
// which vendor, which tab and account every line lands on, which surcharges
// post and why the others don't. Every state explains itself and offers the
// next step: waiting for the Web Connector, in QuickBooks, needs a fix,
// couldn't confirm.

type Target = "desktop" | "online";
const TARGET_NAME: Record<Target, string> = { desktop: "QuickBooks Desktop", online: "QuickBooks Online" };

interface PlanLine {
  lineId: string;
  description: string;
  extendedCents: number;
  posting: "expense" | "item" | "skip";
  account: { id: string; name: string } | null;
  spreadCents: number;
  item: { status: "matched" | "suggested" | "create" | "unmatched"; ref: { id: string; name: string } | null; newName: string; newType: string } | null;
  customerName: string | null;
}
interface PlanSurcharge {
  id: string;
  label: string;
  amountCents: number;
  posts: boolean;
  reason: string;
  account: { id: string; name: string } | null;
}
interface Plan {
  mode: "items" | "expenses";
  vendor: { status: "matched" | "suggested" | "new" | "ambiguous"; ref: { id: string; name: string } | null; newName: string; candidates: Array<{ id: string; name: string }> };
  lines: PlanLine[];
  surcharges: PlanSurcharge[];
  refNumber: string | null;
  txnDate: string | null;
  memo: string;
  postsCents: number;
  expenseTotalCents: number;
  warnings: string[];
  blocking: string[];
}
interface Preview {
  plan: Plan;
  already: ExpensePush | null;
  scanQueued: boolean;
}
interface Lists {
  vendors: Array<{ id: string; name: string }>;
  items: Array<{ id: string; name: string }>;
}
type LineOverride = { lineId: string; resolution: "match" | "create" | "skip"; itemId?: string | null };

function StateLine({ target, state }: { target: Target; state: ExpenseQbState | null }) {
  if (!state) return <p className="text-xs text-steel">Not in {TARGET_NAME[target]} yet.</p>;
  if (state.status === "synced") {
    return (
      <p className="text-xs text-success">
        In {TARGET_NAME[target]}{state.refNumber ? ` as bill ${state.refNumber}` : ""} ·{" "}
        {new Date(state.lastSyncAt).toLocaleString()}
      </p>
    );
  }
  if (state.status === "queued") {
    return (
      <p className="rounded bg-brandorange/10 px-2 py-1 text-xs text-ink">
        {state.lastError ??
          "Waiting for QuickBooks Desktop. The Web Connector picks this up on its next check-in (about a minute); keep QuickBooks open with your company file."}
      </p>
    );
  }
  return (
    <p className="rounded bg-danger/10 px-2 py-1 text-xs text-ink">
      {state.lastError ?? "QuickBooks didn't take this bill."}
    </p>
  );
}

export function ExpenseQbPanel({
  tenantId,
  expenseId,
  expense,
  targets,
}: {
  tenantId: string;
  expenseId: string;
  expense: Expense;
  targets: Target[];
}) {
  const [target, setTarget] = useState<Target>(targets[0]!);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [lists, setLists] = useState<Lists | null>(null);
  const [vendorOverride, setVendorOverride] = useState<{ id: string | null } | undefined>(undefined);
  const [lineOverrides, setLineOverrides] = useState<LineOverride[]>([]);
  const [busy, setBusy] = useState<"preview" | "send" | null>(null);
  const [note, setNote] = useState("");
  const state = target === "desktop" ? expense.qbd : expense.qbo;
  const queued = state?.status === "queued";

  const overrides = { ...(vendorOverride ? { vendor: vendorOverride } : {}), ...(lineOverrides.length ? { lines: lineOverrides } : {}) };

  async function loadPreview() {
    setBusy("preview");
    setNote("");
    try {
      setPreview(await call<Preview>("previewExpensePush", { tenantId, expenseId, target, overrides }));
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  // Re-plan whenever a choice changes (vendor, a line's item).
  useEffect(() => {
    if (preview) void loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(overrides), target]);

  async function loadLists() {
    if (lists) return;
    try {
      setLists(await call<Lists>("listQbLists", { tenantId, target }));
    } catch (err) {
      setNote((err as Error).message);
    }
  }

  async function send(allowResend: boolean) {
    setBusy("send");
    setNote("");
    try {
      await call("pushExpenseToQuickBooks", { tenantId, expenseId, target, overrides, allowResend });
      setPreview(null);
      setNote(target === "desktop" ? "Queued. It posts on the Web Connector's next check-in." : "Posted to QuickBooks Online.");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const plan = preview?.plan;
  const already = preview?.already ?? null;
  const setLine = (o: LineOverride) => setLineOverrides((prev) => [...prev.filter((x) => x.lineId !== o.lineId), o]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-ink">QuickBooks</p>
        {targets.length > 1 ? (
          <Select className="w-48 py-1 text-xs" value={target} onChange={(e) => { setTarget(e.target.value as Target); setPreview(null); }}>
            {targets.map((t) => (
              <option key={t} value={t}>
                {TARGET_NAME[t]}
              </option>
            ))}
          </Select>
        ) : null}
      </div>
      <StateLine target={target} state={state} />
      {state?.warnings?.length && state.status === "synced"
        ? state.warnings.map((w) => (
            <p key={w} className="text-[11px] text-steel">
              {w}
            </p>
          ))
        : null}

      {expense.status !== "approved" ? (
        <p className="text-xs text-steel">Approve the expense to send it to QuickBooks.</p>
      ) : !plan ? (
        <Button variant="secondary" className="px-3 py-1 text-xs" busy={busy === "preview"} disabled={queued} onClick={() => void loadPreview()}>
          {state?.status === "synced" ? "Review & send again" : state ? "Review & send again" : `Review & send to ${TARGET_NAME[target]}`}
        </Button>
      ) : (
        <div className="space-y-2 rounded-lg border border-steel/20 p-2.5 text-xs">
          {preview?.scanQueued ? (
            <p className="rounded bg-steel/10 px-2 py-1 text-ink">
              Reading your QuickBooks vendors, items and accounts. That happens on the Web Connector's next check-in; then
              review again.
            </p>
          ) : null}

          {/* Vendor */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-steel">Vendor:</span>
            <span className="text-ink">
              {plan.vendor.ref
                ? `${plan.vendor.ref.name}${plan.vendor.status === "suggested" ? " (matched by name)" : ""}`
                : plan.vendor.status === "ambiguous"
                  ? "several match, pick one"
                  : `new vendor “${plan.vendor.newName}” is created in QuickBooks`}
            </span>
            {lists ? (
              <Select
                className="w-56 py-0.5 text-xs"
                value={vendorOverride === undefined ? "" : (vendorOverride.id ?? "__new")}
                onChange={(e) =>
                  setVendorOverride(e.target.value === "" ? undefined : { id: e.target.value === "__new" ? null : e.target.value })
                }
              >
                <option value="">Clevis's choice</option>
                <option value="__new">Create “{plan.vendor.newName}”</option>
                {(plan.vendor.candidates.length ? plan.vendor.candidates : lists.vendors).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </Select>
            ) : (
              <button className="text-brandorange-dark underline" onClick={() => void loadLists()}>
                change
              </button>
            )}
          </div>

          <p className="text-steel">
            {plan.mode === "expenses"
              ? `Every line posts to the bill's Expenses tab${plan.lines[0]?.account ? ` → ${plan.lines[0].account.name}` : ""}.`
              : "Lines post as items on the bill's Items tab."}
            {plan.refNumber ? ` Bill no. ${plan.refNumber}.` : " QuickBooks numbers the bill."}
            {plan.txnDate ? ` Dated ${plan.txnDate}.` : ""}
          </p>

          {/* Lines */}
          <div className="divide-y divide-steel/10">
            {plan.lines.map((l) => (
              <div key={l.lineId} className="flex items-center justify-between gap-2 py-1">
                <span className={cn("min-w-0 flex-1 truncate", l.posting === "skip" ? "text-steel line-through" : "text-ink")}>
                  {l.description}
                  {l.customerName ? <span className="text-steel"> · job: {l.customerName}</span> : null}
                  {l.spreadCents ? (
                    <span className="text-steel">
                      {" "}
                      · {l.spreadCents > 0 ? "+" : "−"}
                      {money(Math.abs(l.spreadCents))} spread in
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 text-steel">
                  {l.posting === "expense"
                    ? l.account?.name ?? "—"
                    : l.posting === "skip"
                      ? "skipped"
                      : l.item?.ref
                        ? l.item.ref.name
                        : l.item?.status === "create"
                          ? `new item “${l.item.newName}”`
                          : "pick an item"}
                </span>
                {plan.mode === "items" ? (
                  <Select
                    className="w-32 shrink-0 py-0.5 text-[11px]"
                    value={(() => {
                      const o = lineOverrides.find((x) => x.lineId === l.lineId);
                      return !o ? "" : o.resolution === "match" ? (o.itemId ?? "") : `__${o.resolution}`;
                    })()}
                    onFocus={() => void loadLists()}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) setLineOverrides((prev) => prev.filter((x) => x.lineId !== l.lineId));
                      else if (v === "__create" || v === "__skip") setLine({ lineId: l.lineId, resolution: v.slice(2) as "create" | "skip" });
                      else setLine({ lineId: l.lineId, resolution: "match", itemId: v });
                    }}
                  >
                    <option value="">Clevis's choice</option>
                    <option value="__create">Create new item</option>
                    <option value="__skip">Leave off the bill</option>
                    {(lists?.items ?? []).map((it) => (
                      <option key={it.id} value={it.id}>
                        {it.name}
                      </option>
                    ))}
                  </Select>
                ) : null}
                <span className="w-20 shrink-0 text-right text-ink">{money(l.extendedCents)}</span>
              </div>
            ))}
            {plan.surcharges.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-2 py-1">
                <span className={cn("min-w-0 flex-1 truncate", s.posts ? "text-ink" : "text-steel")}>{s.label}</span>
                <span className="shrink-0 text-steel">{s.posts ? (s.account?.name ?? "needs an account") : s.reason}</span>
                <span className={cn("w-20 shrink-0 text-right", s.posts ? "text-ink" : "text-steel line-through")}>
                  {money(s.amountCents)}
                </span>
              </div>
            ))}
          </div>
          <p className="text-right text-ink">
            Bill total <strong>{money(plan.postsCents)}</strong>
            {plan.postsCents !== plan.expenseTotalCents ? (
              <span className="text-steel"> of {money(plan.expenseTotalCents)}</span>
            ) : null}
          </p>

          {plan.warnings.map((w) => (
            <p key={w} className="text-steel">
              {w}
            </p>
          ))}
          {plan.blocking.map((b) => (
            <p key={b} className="rounded bg-danger/10 px-2 py-1 text-ink">
              {b}
            </p>
          ))}

          <div className="flex items-center justify-between pt-1">
            <button className="inline-flex items-center gap-1 text-steel hover:text-ink" onClick={() => void loadPreview()}>
              <RefreshCw size={12} /> Refresh
            </button>
            <div className="flex items-center gap-2">
              <button className="text-steel underline hover:text-ink" onClick={() => setPreview(null)}>
                Cancel
              </button>
              {already ? (
                <ConfirmButton
                  variant="primary"
                  className="px-3 py-1 text-xs"
                  busy={busy === "send"}
                  disabled={plan.blocking.length > 0}
                  confirm={
                    target === "desktop"
                      ? "This creates a SECOND bill in QuickBooks Desktop. Delete the old one there. Send?"
                      : "Update the bill in QuickBooks Online?"
                  }
                  confirmLabel="Send"
                  onConfirm={() => send(true)}
                >
                  Send again
                </ConfirmButton>
              ) : (
                <Button className="px-3 py-1 text-xs" busy={busy === "send"} disabled={plan.blocking.length > 0} onClick={() => void send(false)}>
                  Send to {TARGET_NAME[target]}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      {note ? <p className="text-xs text-ink">{note}</p> : null}
    </div>
  );
}
